import { useRef } from "react";
import jsPDF from "jspdf";

const MarkdownText = ({ text }) => {
  const formatted = text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");

  return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
};

const ProgressReport = ({ reportData }) => {
  const reportRef = useRef();

  const data = reportData;

  const generatePDF = async () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const leftMargin = 40;
    let y = 60;

    const lineHeight = 16;
    const bottomMargin = 50;
    const pageHeight = doc.internal.pageSize.height - bottomMargin;

    const addText = (
      text,
      size = 12,
      bold = false,
      indent = 0,
      color = "#000000"
    ) => {
      if (!text) return;
      const splitText = doc.splitTextToSize(text, 520 - indent);
      if (y + splitText.length * lineHeight > pageHeight) {
        doc.addPage();
        y = 60;
      }
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      doc.setTextColor(color);
      doc.text(splitText, leftMargin + indent, y);
      y += splitText.length * lineHeight + 6; // tighter spacing
    };

    const report = data.report_json;

    // Title
    addText("Student Performance Report", 20, true, 0, "#2E3A59");
    y += 8;

    // Summary
    addText("Summary Overview", 16, true, 0, "#1B73E8");
    addText(report.summary_overview, 12, false, 10);

    // Sections
    addText("Sections", 16, true, 0, "#1B73E8");
    Object.entries(report.sections).forEach(([sectionName, sectionData]) => {
      addText(sectionName.toUpperCase(), 14, true, 10, "#333");
      addText("Overview", 13, true, 20, "#555");
      addText(sectionData.overview, 12, false, 30);
      addText("Recommendations", 13, true, 20, "#555");
      addText(sectionData.section_recommendation, 12, false, 30);

      addText("Subject Domains", 13, true, 20, "#555");
      Object.entries(sectionData.subject_domains).forEach(
        ([domainName, domainData]) => {
          addText(domainName.replace(/_/g, " "), 13, true, 30, "#00897B");
          addText(domainData.domain_overview, 12, false, 40);

          addText("Topics", 13, true, 40, "#555");
          Object.entries(domainData.domain_topics).forEach(
            ([topicName, topicData]) => {
              addText(topicName.replace(/_/g, " "), 12, true, 50, "#6A1B9A");
              addText("Current Status", 12, true, 60, "#757575");
              addText(topicData.topic_current_status, 11, false, 70);
              addText("Recommendations", 12, true, 60, "#757575");
              addText(topicData.topic_recommendations, 11, false, 70);
            }
          );
        }
      );
    });

    // Priority Roadmap (supports Markdown-style **bold** and line breaks)
    addText("Priority Roadmap", 16, true, 0, "#1B73E8");

    const formatMarkdown = (text) => {
      if (!text) return "";
      // Replace **bold** with plain text but track positions
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return { text: part.slice(2, -2), bold: true };
        }
        return { text: part, bold: false };
      });
    };

    const addMarkdownText = (
      text,
      size = 12,
      indent = 10,
      color = "#000000"
    ) => {
      const segments = formatMarkdown(text);
      doc.setFontSize(size);
      doc.setTextColor(color);
      let lineX = leftMargin + indent;
      segments.forEach((seg) => {
        doc.setFont("helvetica", seg.bold ? "bold" : "normal");
        const splitText = doc.splitTextToSize(seg.text, 520 - indent);
        splitText.forEach((line) => {
          if (y + lineHeight > pageHeight) {
            doc.addPage();
            y = 60;
          }
          doc.text(line, lineX, y);
          y += lineHeight;
        });
      });
      y += 6;
    };

    addMarkdownText(report.priority_roadmap);

    // Next Steps
    addText("Next Steps", 16, true, 0, "#1B73E8");
    addText(report.next_steps, 12, false, 10);

    // Heatmaps (preserve real image ratio)
    addText("Heatmaps", 16, true, 0, "#1B73E8");

    for (const [key, url] of Object.entries(data.heatmaps)) {
      const fullUrl = `${process.env.REACT_APP_AI_URL}${url}`;
      addText(key.toUpperCase(), 13, true, 10, "#333");
      try {
        const imageData = await toBase64(fullUrl);
        if (imageData) {
          const img = new Image();
          img.src = imageData;
          await new Promise((r) => (img.onload = r));

          const pageWidth = doc.internal.pageSize.getWidth();
          const maxWidth = pageWidth - leftMargin * 2;
          const aspectRatio = img.height / img.width;
          const imgWidth = maxWidth;
          const imgHeight = maxWidth * aspectRatio;

          if (y + imgHeight > pageHeight) {
            doc.addPage();
            y = 60;
          }

          doc.addImage(imageData, "JPEG", leftMargin, y, imgWidth, imgHeight);
          y += imgHeight + 20;
        }
      } catch (err) {
        addText(`(Image failed to load: ${fullUrl})`, 10, false, 20, "red");
      }
    }

    // Save
    doc.save("Student_Report.pdf");
  };

  // Helper — load remote image to Base64
  const toBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = (e) => reject(e);
    });
  };

  const renderTopics = (topics) => (
    <div className="ps-3">
      {Object.entries(topics).map(([topicKey, topic]) => (
        <div key={topicKey} className="mb-3">
          <h6 className="fw-semibold text-primary text-capitalize">
            {topicKey.replaceAll("_", " ")}
          </h6>
          <p className="mb-1">
            <strong>Current Status:</strong> {topic.topic_current_status}
          </p>
          <p>
            <strong>Recommendations:</strong> {topic.topic_recommendations}
          </p>
        </div>
      ))}
    </div>
  );

  const renderDomains = (domains) => (
    <div className="ps-2">
      {Object.entries(domains).map(([domainKey, domain]) => (
        <div key={domainKey} className="border rounded p-3 mb-4 bg-light">
          <h5 className="fw-bold text-dark text-capitalize mb-2">
            {domainKey.replaceAll("_", " ")}
          </h5>
          <p>
            <strong>Overview:</strong> {domain.domain_overview}
          </p>
          {domain.domain_topics && (
            <>
              <h6 className="mt-3 mb-2 text-decoration-underline">Topics:</h6>
              {renderTopics(domain.domain_topics)}
            </>
          )}
        </div>
      ))}
    </div>
  );

  const renderSections = (sections) => (
    <>
      {Object.entries(sections).map(([sectionKey, section]) => (
        <div key={sectionKey} className="mb-5">
          <h3 className="text-primary text-capitalize">{sectionKey}</h3>
          <p>
            <strong>Overview:</strong> {section.overview}
          </p>
          <p>
            <strong>Recommendation:</strong> {section.section_recommendation}
          </p>

          {section.subject_domains && (
            <>
              <h4 className="mt-4 mb-3">Subject Domains</h4>
              {renderDomains(section.subject_domains)}
            </>
          )}
        </div>
      ))}
    </>
  );

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-dark">Student Performance Report</h2>
        <button className="btn btn-primary" onClick={generatePDF}>
          Download PDF
        </button>
      </div>

      <div ref={reportRef} className="bg-white p-4 rounded shadow-sm">
        <section className="mb-5">
          <h3>Summary Overview</h3>
          <p>{reportData.report_json.summary_overview}</p>
        </section>

        <section className="mb-5">
          <h3>Sections</h3>
          {renderSections(reportData.report_json.sections)}
        </section>

        <section className="mb-5">
          <h3>Priority Roadmap</h3>
          <p>
            <MarkdownText text={reportData.report_json.priority_roadmap} />
          </p>
        </section>

        <section className="mb-5">
          <h3>Next Steps</h3>
          <p>{reportData.report_json.next_steps}</p>
        </section>

        <section className="mb-5">
          <h3>Heatmaps</h3>
          <div className="row">
            {Object.entries(reportData.heatmaps).map(([key, img]) => (
              <div key={key} className="col-md-12 text-center mb-4">
                <p className="text-capitalize fw-semibold">{key}</p>
                <img
                  src={`${process.env.REACT_APP_AI_API_URL}${img}`}
                  alt={`${key} heatmap`}
                  className="img-fluid rounded border"
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProgressReport;
